import React from "react"
import _ from "lodash"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"

// import { CustomAttributeTypeAPI, AttributeDataTypesAPI } from "../../../../api/types"
import { GetApi } from "../../../../api/api"
import Button from "../../../../components/Button"
import Table, {
  AddButton,
  ActionPopover,
  ActionPopoverButton,
} from "../../../../components/Table"
import AppContext from "../../../../components/AppContext"
import Badge, { LockBadge } from "../../../../components/Badge"

import EditModal from "./components/EditModal"
import { disableIfIntroducedInPreviousVersionOrOverride } from "../../../../components/disableInputPolicy"
import { UiTypes, UiToApi, ApiToUi } from "./components/UIModel"

interface PropertiesProps {
  fccId: string
  atgId: number
  // attributeTypes: CustomAttributeTypeAPI[]
  attributeTypes: UiTypes.CustomAttributeType[]
  currentVersion: number
  maximumStreamVersion: string
  refresh: () => void
  disabled: boolean
  setDisabled: (value: boolean) => void
}

interface PropertiesState {
  showEditModal: boolean
  isNewProp?: boolean
  editProp?: UiTypes.CustomAttributeType
  disabled: boolean
}

class Properties extends React.Component<PropertiesProps, PropertiesState> {
  public static contextType = AppContext

  constructor(props: PropertiesProps) {
    super(props)
    this.state = {
      showEditModal: false,
      disabled: props.disabled,
    }
  }

  public componentDidUpdate(prevProps: PropertiesProps) {
    if (!_.isEqual(prevProps, this.props)) {
      this.setState({
        disabled: this.props.disabled,
      })
    }
  }

  public render() {
    // TODO: This should take the default value into account as well!
    const getRange = (at: UiTypes.CustomAttributeType) => {
      switch (at.dataType) {
        case "Measure Number":
          return renderMeasureNumberSummary(at)
        case "Decimal Number":
          return renderDecimalNumberSummary(at)
        case "Number":
          return renderNumberSummary(at)
        case "Text":
          return renderTextSummary(at)
        case "Dropdown Choice":
          if (at.hasLegalValues) {
            return renderDropdownChoiceSummary(at)
          }
        default:
          return ""
      }
    }

    const rowData = this.props.attributeTypes.map((at) => {
      return {
        icon: (
          <span className="d-flex justify-content-center">
            <i className="modus-icons">reports</i>
          </span>
        ),
        name: at.name,
        type: at.dataType,
        allowedValues: getRange(at),
        action: (
          <this.EditButtons
            at={at}
            currentVersion={this.props.currentVersion}
            disabled={this.state.disabled}
          />
        ),
      }
    })

    const columnData = [
      {
        Header: "",
        accessor: "icon",
        width: 70,
        sortable: false,
      },
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Type",
        accessor: "type",
      },
      {
        Header: "Allowed Values",
        accessor: "allowedValues",
        sortable: false,
      },
      {
        Header: (
          <AddButton
            onClick={this.createAttributeType}
            disabled={this.state.disabled}
          />
        ),
        accessor: "action",
        width: 70,
        sortable: false,
      },
    ]

    return (
      <Row className="d-flex flex-fill justify-content-center">
        {this.state.showEditModal && (
          <EditModal
            atId={this.state.editProp!.id}
            fccId={this.props.fccId}
            atgId={this.props.atgId}
            currentVersion={this.props.currentVersion}
            maximumStreamVersion={this.props.maximumStreamVersion}
            toggle={this.toggleEditModal}
            isOpen={this.state.showEditModal}
            headerText={this.state.isNewProp ? "New Property" : "Edit Property"}
            toggleText={this.state.isNewProp ? "Create" : "Done"}
            onCancel={
              this.state.isNewProp
                ? () => this.deleteAttributeType(this.state.editProp!)
                : undefined
            }
            size="xl"
          />
        )}
        {rowData.length > 0 ? (
          <Col xs={12}>
            <Table data={rowData} columns={columnData} />
          </Col>
        ) : (
          <Col xs={12} className="my-auto text-center">
            <p className="mb-4 text-muted">
              It seems like you haven't created a property yet, press the button
              to add your first property.
            </p>
            <Button
              variant="primary"
              onClick={this.createAttributeType}
              disabled={this.state.disabled}
            >
              New Property
            </Button>
          </Col>
        )}
      </Row>
    )
  }

  private EditButtons = (props: {
    at: UiTypes.CustomAttributeType
    currentVersion?: number
    disabled: boolean
  }) => {
    const editProperty = () => this.toggleEditModal(props.at)
    const deleteAttributeType = () => this.deleteAttributeType(props.at)

    return (
      <ActionPopover>
        <ActionPopoverButton onClick={editProperty} disabled={props.disabled}>
          <span>Edit</span>
        </ActionPopoverButton>
        <div className="dropdown-divider" />
        <ActionPopoverButton
          variant="danger"
          onClick={deleteAttributeType}
          disabled={disableIfIntroducedInPreviousVersionOrOverride(
            props.at,
            props.currentVersion,
            props.disabled
          )}
        >
          <span>Delete</span>
          {props.at.introducedInVersion !== props.currentVersion && (
            <LockBadge className="ml-3" variant="danger" />
          )}
        </ActionPopoverButton>
      </ActionPopover>
    )
  }

  private createAttributeType = async () => {
    this.setDisabled(true)
    try {
      const api = await GetApi()
      const attributeType = await api.createAttributeType(
        this.props.fccId,
        this.props.atgId,
        UiToApi.convertAttributeTypeCreateRequest({
          name: "NewProperty",
          caption: "NewProperty", // Caption is for now same as name
          dataType: "Text",
        })
      )
      this.toggleEditModal(ApiToUi.convertAttributeType(attributeType), true)
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private deleteAttributeType = async (at: UiTypes.CustomAttributeType) => {
    this.setDisabled(true)
    try {
      const api = await GetApi()
      if (this.state.showEditModal === true) {
        this.toggleEditModal(at, true)
        await api.deleteAttributeType(this.props.fccId, at.id)
        this.props.refresh()
      } else {
        if (window.confirm("Are you sure you want to delete this property?")) {
          await api.deleteAttributeType(this.props.fccId, at.id)
          this.props.refresh()
          this.context.useToasts.addToast(
            `Successfully deleted the property '${at.name}'.`,
            "success",
            true
          )
        }
      }
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private toggleEditModal = (
    at?: UiTypes.CustomAttributeType,
    isNew: boolean = false
  ) => {
    this.setState(
      (prevState) => ({
        isNewProp: isNew,
        editProp: at,
        showEditModal: !prevState.showEditModal,
      }),
      () => {
        if (this.state.showEditModal === false && !this.state.isNewProp) {
          this.props.refresh()
        }
      }
    )
  }

  private setDisabled = (value: boolean) => {
    this.props.setDisabled(value)
    this.setState({
      disabled: value,
    })
  }
}

const renderDropdownChoiceSummary = (at: UiTypes.CustomAttributeType) =>
  at.legalValues
    ?.map((lv) => ({ ...lv, no: lv.no || 0 }))
    .sort((a, b) => a.no - b.no)
    .map((lv, i) => (
      <Badge key={i} className="ml-1" pill={true} variant="secondary">
        {lv.value}
      </Badge>
    ))

const renderDecimalNumberSummary = (at: UiTypes.CustomAttributeType) =>
  renderNumberSummary(at)

const renderMeasureNumberSummary = (at: UiTypes.CustomAttributeType) => (
  <React.Fragment>
    {at.defaultValue && (
      <Badge className="ml-1" pill={true} variant="secondary">
        {at.defaultValue}
      </Badge>
    )}
    {at.minValue && (
      <Badge className="ml-1" pill={true} variant="secondary">
        min: {at.minValue}
      </Badge>
    )}
    {at.maxValue && (
      <Badge className="ml-1" pill={true} variant="secondary">
        max: {at.maxValue}
      </Badge>
    )}
    {at.measureType && (
      <Badge className="ml-1" pill={true} variant="secondary">
        measureType: {at.measureType}
      </Badge>
    )}
  </React.Fragment>
)

const renderNumberSummary = (at: UiTypes.CustomAttributeType) => (
  <React.Fragment>
    {at.defaultValue && (
      <Badge className="ml-1" pill={true} variant="secondary">
        {at.defaultValue}
      </Badge>
    )}
    {at.minValue && (
      <Badge className="ml-1" pill={true} variant="secondary">
        min: {at.minValue}
      </Badge>
    )}
    {at.maxValue && (
      <Badge className="ml-1" pill={true} variant="secondary">
        max: {at.maxValue}
      </Badge>
    )}
  </React.Fragment>
)

const renderTextSummary = (at: UiTypes.CustomAttributeType) =>
  at.defaultValue && (
    <Badge className="ml-1" pill={true} variant="secondary">
      {at.defaultValue}
    </Badge>
  )

export default Properties
